import { Modal } from './Modal.js';
import { prefersReducedMotion } from './motion.js';
import type { AssetLoader } from './AssetLoader.js';
import type { GameObject, Milestone } from '../types.js';

const CONFETTI_COUNT = 40;

export class LevelUpWindow {
    private readonly assetLoader: AssetLoader;
    private readonly modal: Modal;
    private readonly badge: HTMLElement;
    private readonly era: HTMLElement;
    private readonly message: HTMLElement;
    private readonly unlocks: HTMLElement;
    private readonly confetti: HTMLElement;

    constructor({ assetLoader }: { assetLoader: AssetLoader }) {
        this.assetLoader = assetLoader;
        this.modal = new Modal({ title: '', variant: 'levelup' });

        const layout = document.createElement('div');
        layout.className = 'levelup';

        this.badge = document.createElement('div');
        this.badge.className = 'levelup__badge';
        layout.appendChild(this.badge);

        const heading = document.createElement('div');
        heading.className = 'levelup__heading';
        heading.textContent = 'LEVEL UP';
        layout.appendChild(heading);

        this.era = document.createElement('div');
        this.era.className = 'levelup__era';
        layout.appendChild(this.era);

        this.message = document.createElement('p');
        this.message.className = 'levelup__message';
        layout.appendChild(this.message);

        this.unlocks = document.createElement('div');
        this.unlocks.className = 'levelup__unlocks';
        layout.appendChild(this.unlocks);

        this.confetti = document.createElement('div');
        this.confetti.className = 'levelup__confetti';
        this.confetti.setAttribute('aria-hidden', 'true');
        layout.appendChild(this.confetti);

        this.modal.body.appendChild(layout);
    }

    private fillConfetti(): void {
        this.confetti.innerHTML = '';
        if (prefersReducedMotion()) return;

        const colours = ['#ffc949', '#e0603c', '#6fc36f', '#63b3ed', '#c78bea'];

        for (let i = 0; i < CONFETTI_COUNT; i++) {
            const piece = document.createElement('i');
            piece.style.left = `${(i / CONFETTI_COUNT) * 100}%`;
            piece.style.background = colours[i % colours.length]!;
            piece.style.animationDelay = `${(i % 7) * 0.11}s`;
            piece.style.animationDuration = `${1.6 + (i % 5) * 0.22}s`;
            piece.style.setProperty('--drift', `${(i % 9) * 8 - 32}px`);
            piece.style.setProperty('--spin', `${(i % 4) * 220 + 180}deg`);
            this.confetti.appendChild(piece);
        }
    }

    open(milestone: Milestone, unlocked: GameObject[] = []): void {
        const popup = milestone.popup ?? { title: '', message: '' };

        this.badge.innerHTML = '';
        if (popup.image && this.assetLoader.getImage(popup.image)) {
            const img = document.createElement('img');
            img.src = this.assetLoader.resolve(popup.image);
            img.alt = '';
            this.badge.appendChild(img);
        }

        this.era.textContent = popup.title ?? '';
        this.era.classList.toggle('is-empty', !popup.title);

        this.message.textContent = popup.message ?? '';
        this.message.classList.toggle('is-empty', !popup.message);

        this.unlocks.innerHTML = '';
        unlocked.forEach((object) => {
            const chip = document.createElement('span');
            chip.className = 'levelup__chip';
            chip.textContent = object.label || object.id;
            this.unlocks.appendChild(chip);
        });

        this.fillConfetti();
        this.modal.open();
    }

    close(): void {
        this.modal.close();
    }
}
